import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { env } from '@/lib/env';
import type { PageMeta } from '@/types/api';

import { endpoints } from './endpoints';
import { ApiError, parseApiError, UnauthorizedError } from './errors';
import { tokenStore } from './token-store';

type RetryConfig = AxiosRequestConfig & { _retried?: boolean };

/** Endpoints whose own 401 means "bad input"/"no session yet", not "stale session" —
 *  never refresh-retry these. Prefix matches cover login (incl. otp send/verify) and the
 *  whole forgot-password flow (send/verify/reset). */
const AUTH_ENTRY_PATHS = [
  '/auth/tracking/login',
  '/auth/tracking/forgot-password',
  endpoints.auth.refresh,
];

function isAuthEntry(url: string | undefined): boolean {
  return !!url && AUTH_ENTRY_PATHS.some((p) => url.includes(p));
}

export function createApiClient(baseURL: string): AxiosInstance {
  const instance = axios.create({ baseURL, withCredentials: true });
  let refreshInFlight: Promise<void> | null = null;

  instance.interceptors.request.use((config) => {
    const token = tokenStore.getAccessToken();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  });

  const doRefresh = async () => {
    if (!refreshInFlight) {
      refreshInFlight = axios
        .post<{ access_token?: string }>(`${baseURL}${endpoints.auth.refresh}`, null, {
          withCredentials: true,
        })
        .then((res) => {
          if (res.data?.access_token) tokenStore.setAccessToken(res.data.access_token);
        })
        .finally(() => {
          refreshInFlight = null;
        });
    }
    return refreshInFlight;
  };

  instance.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const original = error.config as RetryConfig | undefined;
      if (!error.response || !original) return Promise.reject(error);
      const { status, data } = error.response;

      if (status === 401 && !original._retried && !isAuthEntry(original.url)) {
        original._retried = true;
        try {
          await doRefresh();
        } catch {
          tokenStore.clear();
          return Promise.reject(new UnauthorizedError('Refresh failed', data));
        }
        return instance.request(original);
      }

      const parsed = parseApiError(status, data, error.message);
      if (status === 401) {
        const unauthorizedError = new UnauthorizedError(parsed.message, data);
        unauthorizedError.code = parsed.code;
        unauthorizedError.details = parsed.details;
        return Promise.reject(unauthorizedError);
      }
      return Promise.reject(
        new ApiError(parsed.message, status, { code: parsed.code, details: parsed.details, data }),
      );
    },
  );

  return instance;
}

/** The deep success-side seam over an axios instance. Strips the transport wrapper
 *  (`res.data`) and the `{ data }` success envelope so no call site peels by hand;
 *  errors still propagate from the instance's interceptor as `ApiError`. Validation
 *  stays at the call site (feature schemas), so the verbs return raw `T`/`unknown`. */
export type ApiRequest = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) => Promise<T>;
  /** List endpoints keep the `{ data, meta }` envelope — `meta` carries pagination. */
  getList: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ) => Promise<{ data: T[]; meta: PageMeta }>;
  post: <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  put: <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  patch: <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  delete: (url: string, config?: AxiosRequestConfig) => Promise<void>;
};

export function createApiRequest(instance: AxiosInstance): ApiRequest {
  return {
    get: async <T = unknown>(url: string, config?: AxiosRequestConfig) =>
      (await instance.get<{ data: T }>(url, config)).data.data,
    getList: async <T = unknown>(url: string, config?: AxiosRequestConfig) =>
      (await instance.get<{ data: T[]; meta: PageMeta }>(url, config)).data,
    post: async <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
      (await instance.post<{ data: T }>(url, body, config)).data.data,
    put: async <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
      (await instance.put<{ data: T }>(url, body, config)).data.data,
    patch: async <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
      (await instance.patch<{ data: T }>(url, body, config)).data.data,
    delete: async (url: string, config?: AxiosRequestConfig) => {
      await instance.delete(url, config);
    },
  };
}

export const apiClient = createApiRequest(createApiClient(env.VITE_API_URL));
